import { useEffect, useState } from 'react'
import { Shell, useShellMeta } from './Shell.js'
import { apiGet } from './primitives.js'
import { SettingsCrumb } from './SettingsCrumb.js'

/**
 * Pages Paramètres secondaires (LOT 4) — préparées proprement, sans
 * inventer : chaque information affichée existe réellement, et ce qui
 * n'existe pas encore est dit tel quel.
 */

function PageCadre(props: { titre: string; soustitre: string; children: React.ReactNode }) {
  const meta = useShellMeta()
  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger">
        <SettingsCrumb />
        <h1 className="lc-title">{props.titre}</h1>
        <p className="lc-subtitle">{props.soustitre}</p>
        {props.children}
      </main>
    </Shell>
  )
}

function Fait(props: { label: string; value: React.ReactNode }) {
  return (
    <div className="ad-fact">
      <span className="ad-fact__label">{props.label}</span>
      <span className="ad-fact__value">{props.value}</span>
    </div>
  )
}

interface Summary {
  system: {
    version: string; migrations: number
    worker_heartbeat_at: string | null; worker_alive: boolean
    auth_enforcement: boolean; global_write: boolean; maintenance: boolean
  }
  integrations: { configured: number; rh_pending: number }
}

function useSummary(): Summary | null {
  const [s, setS] = useState<Summary | null>(null)
  useEffect(() => { apiGet<Summary>('/api/v1/admin/summary').then(setS).catch(() => undefined) }, [])
  return s
}

/*
 * /settings/integrations a quitté ce fichier (15/10/2026).
 *
 * L'ancienne page « Intégrations » (liste + âge des secrets) et la section
 * Shotgun n'étaient plus atteignables depuis le 31/08 : l'adresse menait au
 * catalogue. L'âge de chaque clé vit désormais SUR le catalogue
 * (IntegrationsCatalogPage), la section Shotgun sur sa propre page
 * (ShotgunIntegrationPage), avec le panneau « Clé d'accès ».
 */

/* ─────────────────────────── /settings/system ───────────────────────── */
export function SystemPage() {
  const s = useSummary()
  return (
    <PageCadre titre="Système" soustitre="L’état technique de Gestion — uniquement ce qui est réellement mesuré.">
      <section className="lc-panel gd-panel"><div className="lc-panel__body lc-panel__body--flush ad-facts">
        <Fait label="Version" value={<span className="ad-mono">{s ? `v${s.system.version}` : '…'}</span>} />
        <Fait label="Migrations appliquées" value={s ? s.system.migrations : '…'} />
        <Fait label="Worker" value={
          s ? (s.system.worker_alive
            ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Actif</span>
            : <span className="lc-pill lc-pill--error">Silencieux depuis plus de 5 min</span>) : '…'
        } />
        <Fait label="Dernier battement worker" value={
          s?.system.worker_heartbeat_at
            ? new Date(s.system.worker_heartbeat_at).toLocaleTimeString('fr-FR')
            : '—'
        } />
        <Fait label="Mode maintenance" value={s ? (s.system.maintenance ? 'Actif' : 'Inactif') : '…'} />
      </div></section>
      <div className="adm-note" style={{ maxWidth: 680 }}>
        La supervision de l’infrastructure (Dell, Docker, réseau…) arrivera avec
        la famille Infrastructure — rien n’est simulé ici en attendant.
      </div>
    </PageCadre>
  )
}

/*
 * /settings/backups a quitté ce fichier.
 *
 * Ce n'était qu'une page d'information : elle décrivait une procédure
 * opérateur et affirmait qu'« aucune restauration ne se déclenche depuis le
 * navigateur ». C'était juste tant que rien n'avait jamais été testé.
 *
 * Le vrai centre vit désormais dans BackupsPage.tsx — créer, vérifier,
 * télécharger, importer, répéter dans une base jetable, et seulement ensuite
 * restaurer.
 */
